import * as tslib_1 from "tslib";
import Pusher from 'pusher-js';
import { combineLatest, zip, of, forkJoin, Subject } from 'rxjs';
import { switchMap, map, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Selectors, Actions } from 'store/root-reducer';
import { SearchEngineTypeValue } from 'modules/shared/models/screening.models';
import { SearchPushEvent } from 'modules/shared/models/pusher.models';
import { SnackbarService } from './snackbar.service';
import { ScreeningService } from 'modules/core/services/screening.service';
import { CustomerService } from 'modules/core/services/customer.service';
import { environment } from 'environments/environment';
import * as i0 from "@angular/core";
import * as i1 from "@ngrx/store";
import * as i2 from "./snackbar.service";
import * as i3 from "../modules/core/services/screening.service";
import * as i4 from "../modules/core/services/customer.service";
var PusherService = /** @class */ (function () {
    function PusherService(store, snackBar, screeningService, customerService) {
        this.store = store;
        this.snackBar = snackBar;
        this.screeningService = screeningService;
        this.customerService = customerService;
        this.notify$ = new Subject();
        this.screeningComplete$ = new Subject();
        this.specificScreeningComplete$ = new Subject();
        this.quickscanComplete$ = new Subject();
    }
    PusherService.prototype.startSocket = function () {
        this.pusherClient = new Pusher(environment.PUSHER.KEY, {
            cluster: environment.PUSHER.CLUSTER,
            forceTLS: true
        });
    };
    PusherService.prototype.init = function () {
        var _this = this;
        this.store.select(Selectors.auth.getUser).subscribe(function (user) {
            if (user && user.sub) {
                if (!_this.pusherClient) {
                    _this.pusherClient = new Pusher(environment.PUSHER.KEY, {
                        cluster: environment.PUSHER.CLUSTER,
                        forceTLS: true
                    });
                }
                var channel = _this.pusherClient.subscribe(user.sub);
                channel.bind('searches', function (data) {
                    _this.notify$.next(data);
                });
            }
            else {
                _this.stop();
            }
        });
    };
    PusherService.prototype.start = function () {
        this.checkSingleScreeningStatus();
        this.checkCustomersStatus();
        this.checkQuickscanStatus();
    };
    PusherService.prototype.stop = function () {
        if (this.pusherClient) {
            this.pusherClient.disconnect();
            this.pusherClient = null;
        }
    };
    /**
     * Remove finished screening from store and emit observable to notify components
     * @method updateSingleScreeningStatusInStore
     */
    PusherService.prototype.updateSingleScreeningStatusInStore = function (message) {
        var recordId;
        if (message.search_type === 'QUICKSCAN') {
            recordId = message.record_id;
            if (!message.extra_data) {
                return;
            }
            var uuid = message.extra_data['uuid'];
            if (message.engine_type === SearchEngineTypeValue.NLP) {
                this.requestUpdateQuickScan({
                    engineType: SearchEngineTypeValue.INTERNET_SEARCH,
                    quickscanId: recordId
                });
                this.store.dispatch(new Actions.pusher.RemoveQuickscanNlp({
                    id: uuid
                }));
            }
            else {
                this.requestUpdateQuickScan({
                    engineType: message.engine_type,
                    quickscanId: recordId
                });
                this.store.dispatch(new Actions.pusher.RemoveQuickscanSearch({
                    id: uuid,
                    engineType: message.engine_type,
                }));
            }
        }
        else {
            recordId = message.base_url.recordId;
            if (message.engine_type === SearchEngineTypeValue.NLP) {
                this.requestReloadSpecificScreening({
                    recordId: recordId,
                    engineType: SearchEngineTypeValue.INTERNET_SEARCH
                });
                this.store.dispatch(new Actions.pusher.RemoveNlp({
                    customerId: message.base_url.customerId,
                    recordId: recordId
                }));
            }
            else {
                this.requestReloadSpecificScreening({
                    recordId: recordId,
                    engineType: message.engine_type
                });
                this.store.dispatch(new Actions.pusher.RemoveScreening({
                    customerId: message.base_url.customerId,
                    id: recordId,
                    engineType: message.engine_type,
                    generateReport: message.base_url.generateReport
                }));
            }
        }
        // need to test error handling
        if (message.err) {
            this.setScreeningError(message.engine_type, recordId);
        }
        else {
            this.removeScreeningError(message.engine_type, recordId);
        }
    };
    /**
     * Remove finished customers from store
     * @method removeCustomerFromPusherStore
     */
    PusherService.prototype.removeCustomerFromPusherStore = function (customerIdList) {
        this.store.dispatch(new Actions.pusher.RemoveCustomer(customerIdList));
    };
    /**
     * Set error state in store
     * @method updateErrorStore
     */
    PusherService.prototype.setScreeningError = function (engineType, recordId) {
        this.store.dispatch(new Actions.error.SetScreeningError({
            recordId: recordId,
            searchEngineType: engineType
        }));
    };
    /**
     * Remove error state in store
     * @method updateErrorStore
     */
    PusherService.prototype.removeScreeningError = function (engineType, recordId) {
        this.store.dispatch(new Actions.error.RemoveScreeningError({
            recordId: recordId,
            searchEngineType: engineType
        }));
    };
    /**
     * Update search engine credits
     * @method updateBillableStore
     */
    PusherService.prototype.updateBillableStore = function () {
        this.store.dispatch(new Actions.billable.LoadBillable({ quietUpdate: true }));
    };
    /**
     * Generate risk report
     * @method generateRiskReport
     */
    PusherService.prototype.generateRiskReport = function (customerId, customerName, redirectToScreening) {
        this.store.dispatch(new Actions.riskAssessment.CreateRiskReport({
            customerId: customerId,
            customerName: customerName,
            redirectToScreening: redirectToScreening
        }));
    };
    /**
     * Update pusher store when single search finish msg is received from pusher
     * @method checkSingleScreeningStatus
     */
    PusherService.prototype.checkSingleScreeningStatus = function () {
        var _this = this;
        this.onSearchPush()
            .subscribe(function (message) {
            _this.updateSingleScreeningStatusInStore(message);
        });
    };
    /**
     * Show snackbar msg when all screenings of the customer is done
     * @method checkCustomersStatus
     */
    PusherService.prototype.checkCustomersStatus = function () {
        var _this = this;
        // when all searches of a customer finish and no report needs to be generated
        // const customersNeedToBeGeneratedRiskReport: number[] = [];
        combineLatest([
            this.store.select(Selectors.pusher.finishCustomer(SearchPushEvent.NO_GENERATE_REPORT)),
            this.store.select(Selectors.pusher.nlp)
        ])
            .pipe(filter(function (_a) {
            var customers = _a[0], nlpStatus = _a[1];
            return customers.length > 0;
        }), switchMap(function (_a) {
            var customers = _a[0], nlpStatus = _a[1];
            return zip(
            // get CRP name for snackbar message
            forkJoin(customers.map(function (customer) {
                if (customer.screeningRequest.crpId) {
                    return _this.customerService.getCrp(customer.screeningRequest.crpId).pipe(map(function (crp) {
                        customer.screeningRequest.crpName = crp.name;
                        return customer;
                    }));
                }
                return of(customer);
            })), of(nlpStatus));
        }))
            .subscribe(function (_a) {
            var customers = _a[0], nlpStatus = _a[1];
            customers.forEach(function (customer) {
                var engineLabelSubString = '';
                var crpSubString = '';
                // will trigger screening component to dispatch crp and hence update screening conclusion in screening summary component
                _this.requestReloadScreening({ customerId: customer.id, customerName: customer.name });
                if (customer.screeningRequest.engineTypes && customer.screeningRequest.engineTypes.length === 1) {
                    engineLabelSubString = _this.screeningService.getSearchEngineLabel(customer.screeningRequest.engineTypes[0]) + ' ';
                }
                if (customer.screeningRequest.crpName && !customer.screeningRequest.primaryCrp) {
                    crpSubString = customer.screeningRequest.crpName + ' of ';
                }
                // if NLP is enabled, and this customer has NLP in progress, show a different msg to mention NLP
                if (nlpStatus.some(function (nlp) { return nlp.customerId === customer.id && !nlp.isRemoved; })) {
                    _this.snackBar.success(engineLabelSubString + "Screening for " + crpSubString + "customer " + customer.name + " has finished.\n                Please wait for a while to see full NLP result.");
                }
                else {
                    _this.snackBar.success(engineLabelSubString + "Screening for " + crpSubString + "customer " + customer.name + " has finished.");
                }
            });
            if (customers.length > 0) {
                _this.updateBillableStore();
                _this.removeCustomerFromPusherStore(customers.map(function (customer) { return customer.id; }));
            }
        });
        // when all searches of a customer finish and a report needs to be generated
        this.store.select(Selectors.pusher.finishCustomer(SearchPushEvent.GENERATE_REPORT))
            .subscribe(function (customers) {
            customers.forEach(function (customer) {
                if (!customer.error) {
                    _this.generateRiskReport(customer.id, customer.name, customer.screeningRequest.redirectToScreening);
                }
            });
            if (customers.length > 0) {
                _this.updateBillableStore();
                _this.removeCustomerFromPusherStore(customers.map(function (customer) { return customer.id; }));
            }
        });
    };
    PusherService.prototype.checkQuickscanStatus = function () {
        var _this = this;
        combineLatest([
            this.store.select(Selectors.pusher.finishQuickscan),
            this.store.select(Selectors.pusher.quickScanNlp)
        ])
            .subscribe(function (_a) {
            var quickscans = _a[0], quickscanNlpStatus = _a[1];
            quickscans.forEach(function (quickscan) {
                _this.updateBillableStore();
                if (quickscanNlpStatus.some(function (quickscanNlp) { return quickscanNlp.id === quickscan.id; })) {
                    _this.snackBar.success("Quick scan for " + quickscan.name + " has finished. Please wait for a while to see full NLP result.");
                }
                else {
                    _this.snackBar.success("Quick scan for " + quickscan.name + " has finished");
                }
            });
            if (quickscans.length > 0) {
                _this.store.dispatch(new Actions.pusher.RemoveQuickscan(quickscans.map(function (quickscan) { return quickscan.id; })));
            }
        });
    };
    PusherService.prototype.onSearchPushDone = function () {
        return this.screeningComplete$.asObservable();
    };
    PusherService.prototype.onSpecificScreeningDone = function () {
        return this.specificScreeningComplete$.asObservable();
    };
    PusherService.prototype.onQuickScanSearchPushDone = function () {
        return this.quickscanComplete$.asObservable();
    };
    PusherService.prototype.onSearchPush = function (engine) {
        return this.notify$.asObservable().pipe(map(function (pusherMessage) {
            return tslib_1.__assign({}, pusherMessage, { base_url: pusherMessage.base_url ? JSON.parse(pusherMessage.base_url) : null, extra_data: pusherMessage.extra_data ? JSON.parse(pusherMessage.extra_data) : null });
        }));
    };
    PusherService.prototype.requestUpdateQuickScan = function (engineType) {
        this.quickscanComplete$.next(engineType);
    };
    PusherService.prototype.requestReloadScreening = function (event) {
        this.screeningComplete$.next(event);
    };
    PusherService.prototype.requestReloadSpecificScreening = function (event) {
        this.specificScreeningComplete$.next(event);
    };
    PusherService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function PusherService_Factory() { return new PusherService(i0.ɵɵinject(i1.Store), i0.ɵɵinject(i2.SnackbarService), i0.ɵɵinject(i3.ScreeningService), i0.ɵɵinject(i4.CustomerService)); }, token: PusherService, providedIn: "root" });
    return PusherService;
}());
export { PusherService };
